/** Close states (screens §P6, ADR-0019 §3): the fallback banner before close and what went in after it. */
import type { CroAttestation, DraftView, SubmissionSummary, TeamState } from "@bsim/contracts";
import { clockTime } from "../format";


/** The banner text while the countdown is inside the warning window; null when there is nothing to say. */
export function closeBannerText(fallback: DraftView["closeFallback"]): { readonly lead: string; readonly rest: string } | null {
  if (fallback === null) return null;
  return fallback.kind === "lastYearsPlan"
    ? { lead: "No attested version yet.", rest: "If the round closes now, last year's plan goes in." }
    : { lead: "", rest: `If the round closes now, attested v${fallback.attestedDraftVersion ?? "?"} goes in.` };
}

/** The one sentence for a sign (a solo run has one plan and no signer, so it carries no version): the server's own answer (its stored version and time), never the client's assumption. */
export const submittedLine = (submission: SubmissionSummary, declined = false): string => `${declined ? "Signed over the risk officer's decline" : submission.source === "team" && !submission.attested ? "Signed unattested" : "Signed and submitted"}${submission.source === "solo" ? "" : ` v${submission.draftVersion ?? "?"}`} at ${clockTime(submission.submittedAt)}.`;

/** The server's word that this very plan version is signed (it survives a reload): from then on nothing on the page is a live attest or objection, the year runs. */
export const planSigned = (team: TeamState, draft: DraftView): boolean => {
  const latest = team.latestSubmission;
  return latest !== null && (latest.source === "team" || latest.source === "solo") && latest.draftVersion === draft.version;
};

/** After close, by `SubmissionSummary.source`; `forecastCommitId = null` adds the unscored line in a round that has a call (from round 2). */
export function closedLines(submission: SubmissionSummary, callRound = true): readonly string[] {
  const v = submission.draftVersion;
  const signed = submittedLine(submission);
  const main = {
    team: signed,
    deadline_attested: `Round closed. Your last attested package (v${v ?? "?"}) went in.`,
    default_carry_forward: "Round closed. No package was attested, so last year's plan went in.",
    facilitator_rescue: `Round closed. Your facilitator submitted your current draft (v${v ?? "?"}) as unattested.`,
    persona: "Round closed.", autopilot: "Round closed.", solo: signed,
  }[submission.source];
  return callRound && submission.forecastCommitId === null ? [main, "You didn't make your forecast this round, so it isn't scored."] : [main];
}

/** After the plan went in, both seats read the reasons on the record: the risk officer's decline and the CEO's reason for signing over it or over an objection. */
export function reasonLines(submission: SubmissionSummary, attestation: CroAttestation | null): readonly string[] {
  const declined = attestation?.stance === "decline";
  const note = submission.ceoOverrideNote?.trim() ?? "";
  return [
    ...(declined && attestation.statement.trim() !== "" ? [`The risk officer's reason for declining: \u201c${attestation.statement.trim()}\u201d`] : []),
    ...(note === "" ? [] : [`The CEO's reason for signing over the risk officer's ${declined ? "decline" : "objection"}: \u201c${note}\u201d`]),
  ];
}
